import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  PLATFORM_ID,
  signal,
  viewChild,
} from '@angular/core';
import { isPlatformBrowser, NgTemplateOutlet } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';
import { ArrowRight, X, LucideIconData } from 'lucide-angular';
import { IconComponent } from '../../../../shared/ui/icon/icon.component';
import { ButtonComponent } from '../../../../shared/ui/button/button.component';
import { SearchBarComponent } from '../../../../shared/ui/search-bar/search-bar.component';
import { CartService } from '../../../../shared/services/cart.service';
import { CartDrawerService } from '../../../../shared/services/cart-drawer.service';
import { StockAlertModalService } from '../../../../shared/services/stock-alert-modal.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { cartErrorMessage } from '../../../../shared/utils/cart.utils';
import { Product } from '../../../../shared/models/product.model';
import { SearchOverlayService } from '../../data/search-overlay.service';
import { SearchResultRowComponent } from '../search-result-row/search-result-row.component';
import { SearchSkeletonRowComponent } from '../search-skeleton-row/search-skeleton-row.component';

const SKELETON_ROWS = [0, 1, 2, 3, 4];
// Matches the header's mobile switch (< 1024px) so landscape phones also get the
// full-screen search sheet instead of the desktop dropdown (which would overlap
// the sticky mobile search row).
const MOBILE_BREAKPOINT = '(max-width: 1023px)';
const CORGI_MASCOT_URL = '/images/logo/empty_cart.png';

@Component({
  selector: 'app-search-overlay',
  imports: [
    NgTemplateOutlet,
    IconComponent,
    ButtonComponent,
    SearchBarComponent,
    SearchResultRowComponent,
    SearchSkeletonRowComponent,
  ],
  templateUrl: './search-overlay.component.html',
  styleUrl: './search-overlay.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.is-mobile]': 'isMobile()',
    '[attr.aria-hidden]': '!service.isOpen()',
  },
})
export class SearchOverlayComponent {
  protected readonly service = inject(SearchOverlayService);
  private readonly cartService = inject(CartService);
  private readonly cartDrawerService = inject(CartDrawerService);
  private readonly stockAlertModal = inject(StockAlertModalService);
  private readonly toastService = inject(ToastService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly skeletonRows = SKELETON_ROWS;
  protected readonly mascotUrl = CORGI_MASCOT_URL;
  protected readonly arrowIcon: LucideIconData = ArrowRight;
  protected readonly closeIcon: LucideIconData = X;

  // Vive en el service para que el header pueda consultarlo; aquí lo aliasamos
  // para el template, el host binding y el focus-trap.
  protected readonly isMobile = this.service.isMobile;

  // Diferimos el anuncio de "{n} resultados" para que los lectores de pantalla
  // no se sobrecarguen con cada tecla. Se actualiza sólo cuando status pasa
  // de loading → success/empty.
  private readonly liveMessageSig = signal('');
  protected readonly liveMessage = this.liveMessageSig.asReadonly();

  private readonly sheetRef = viewChild<ElementRef<HTMLElement>>('sheet');
  private pendingAdd = signal<Set<string | number>>(new Set());

  protected readonly showRecents = computed(() => {
    const q = this.service.query().trim();
    return q.length === 0 && this.service.recent().length > 0;
  });

  protected readonly showEmptyHint = computed(() => {
    const q = this.service.query().trim();
    return q.length === 0 && this.service.recent().length === 0;
  });

  constructor() {
    if (!isPlatformBrowser(this.platformId)) return;

    // matchMedia → signal. Evita BreakpointObserver (no hay CDK instalado).
    afterNextRender(() => {
      const mq = window.matchMedia(MOBILE_BREAKPOINT);
      this.service.setMobile(mq.matches);
      const handler = (e: MediaQueryListEvent) => this.service.setMobile(e.matches);
      mq.addEventListener('change', handler);
      this.destroyRef.onDestroy(() => mq.removeEventListener('change', handler));
    });

    // Keyboard: Esc cierra, ↑↓ mueven highlight, Enter selecciona.
    // Se engancha a document porque el foco vive en el input del search-bar,
    // no dentro del overlay — sólo interceptamos cuando el panel está abierto.
    afterNextRender(() => {
      fromEvent<KeyboardEvent>(document, 'keydown')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((e) => {
          if (!this.service.isOpen()) return;
          if (e.key === 'Escape') {
            e.preventDefault();
            this.service.close();
            return;
          }
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            this.service.moveHighlight(1);
            return;
          }
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            this.service.moveHighlight(-1);
            return;
          }
        });
    });

    // Click-outside: cierra el overlay cuando el usuario hace pointerdown
    // fuera de cualquier search-bar y fuera del overlay. Complementa el
    // cierre basado en `blur` del input (que cubre Tab y cambios de foco),
    // ya que en algunos escenarios el blur no llega a disparar close() de
    // forma fiable (p. ej. clicks rápidos, navegación programática, o
    // elementos de la página que no mueven foco al hacer click).
    afterNextRender(() => {
      fromEvent<PointerEvent>(document, 'pointerdown')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((e) => {
          if (!this.service.isOpen()) return;
          // En mobile el backdrop del sheet ya maneja el cierre.
          if (this.isMobile()) return;
          const target = e.target as HTMLElement | null;
          if (!target) return;
          // Permitir clicks dentro del overlay o sobre cualquier search-bar
          // (incluye los iconos lupa y X, que están dentro de `.search-bar`).
          if (target.closest('.search-overlay') || target.closest('.search-bar')) {
            return;
          }
          this.service.close();
        });
    });

    // Lock del scroll del body mientras el sheet mobile está abierto.
    effect(() => {
      if (!isPlatformBrowser(this.platformId)) return;
      const locked = this.service.isOpen() && this.isMobile();
      document.body.style.overflow = locked ? 'hidden' : '';
    });
    this.destroyRef.onDestroy(() => {
      if (isPlatformBrowser(this.platformId)) document.body.style.overflow = '';
    });

    // Focus trap — sólo en mobile sheet.
    afterNextRender(() => {
      fromEvent<KeyboardEvent>(document, 'keydown')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((e) => {
          if (e.key !== 'Tab') return;
          if (!this.service.isOpen() || !this.isMobile()) return;
          const sheet = this.sheetRef()?.nativeElement;
          if (!sheet) return;
          const focusables = this.getFocusableElements(sheet);
          if (focusables.length === 0) return;
          const first = focusables[0];
          const last = focusables[focusables.length - 1];
          const active = document.activeElement as HTMLElement | null;
          if (!active || !sheet.contains(active)) {
            e.preventDefault();
            first.focus();
            return;
          }
          if (e.shiftKey && active === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && active === last) {
            e.preventDefault();
            first.focus();
          }
        });
    });

    // Nota: no guardamos/restauramos foco al cerrar. El input del search-bar
    // ya tiene el foco mientras el overlay está abierto (nunca se lo robamos),
    // así que al cerrar, el foco ya está donde corresponde — donde el usuario
    // hizo click. Restaurar foco al input provocaría un loop blur→close→focus→open.

    // Anuncia "{n} resultados" cuando la búsqueda resuelve con éxito.
    effect(() => {
      const s = this.service.status();
      const total = this.service.totalCount();
      if (s === 'success') {
        this.liveMessageSig.set(`${total} resultado${total === 1 ? '' : 's'}`);
      } else if (s === 'empty') {
        this.liveMessageSig.set('Sin resultados');
      } else if (s === 'error') {
        this.liveMessageSig.set('Error al buscar');
      } else {
        this.liveMessageSig.set('');
      }
    });
  }

  protected rowId(index: number): string {
    return `search-result-row-${index}`;
  }

  protected activeRowId(): string | null {
    const idx = this.service.highlightedIndex();
    return idx >= 0 ? this.rowId(idx) : null;
  }

  protected onCommit(): void {
    this.service.commitSearch(this.service.query());
  }

  protected onRetry(): void {
    this.service.retry();
  }

  protected onRecentClick(term: string, event: MouseEvent): void {
    event.preventDefault();
    this.service.commitRecent(term);
  }

  protected onRecentRemove(term: string, event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.service.removeRecent(term);
  }

  protected onRecentClear(event: MouseEvent): void {
    event.preventDefault();
    this.service.clearRecent();
  }

  protected onSelectRow(product: Product): void {
    this.service.navigateToProduct(product);
  }

  protected onQuickAdd(product: Product): void {
    const id = product.id;
    if (this.pendingAdd().has(id)) return;
    this.markPending(id);

    this.cartService.addLine(Number(id), 1, product).subscribe({
      next: () => {
        this.clearPending(id);
        this.cartDrawerService.open();
      },
      error: (err) => {
        this.clearPending(id);
        // `null` = bloqueo de Acceso VIP: el diálogo ya lo explica.
        const message = cartErrorMessage(err, 'No pudimos agregar el producto al carrito.');
        if (message) this.toastService.show({ type: 'error', message });
      },
    });
  }

  protected onNotifyRequest(product: Product): void {
    // Cerramos el overlay primero para que el modal salga limpio sin la sheet
    // del search ocupando z-index ni body overflow encima.
    this.service.close();
    this.stockAlertModal.open(product);
  }

  protected onNotifyCancel(product: Product): void {
    this.service.close();
    this.stockAlertModal.cancel(product);
  }

  protected onSheetBackdrop(): void {
    this.service.close();
  }

  protected onSheetContentClick(event: MouseEvent): void {
    // Evita que el click en el contenido burbujee al backdrop.
    event.stopPropagation();
  }

  protected onClose(): void {
    this.service.close();
  }

  // ── Input propio del sheet mobile ───────────────────────────────────────
  // El sheet ocupa todo el viewport, así que necesita su propio input (el del
  // header queda tapado). Estos handlers replican el cableado del header.
  protected onSheetQuery(query: string): void {
    this.service.setQuery(query);
  }

  protected onSheetSearch(query: string): void {
    this.service.submitQuery(query);
  }

  protected onSheetSubmit(query: string): void {
    this.service.setQuery(query);
    this.service.selectHighlighted();
  }

  private markPending(id: string | number): void {
    this.pendingAdd.update((set) => {
      const next = new Set(set);
      next.add(id);
      return next;
    });
  }

  private clearPending(id: string | number): void {
    this.pendingAdd.update((set) => {
      const next = new Set(set);
      next.delete(id);
      return next;
    });
  }

  private getFocusableElements(container: HTMLElement): HTMLElement[] {
    const selector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');
    return Array.from(container.querySelectorAll<HTMLElement>(selector)).filter(
      (el) => el.offsetParent !== null,
    );
  }
}
